// Module `help`: the in-app help centre (docs/v3/FEEDBACK-PLAN.md, phase 1).
// Guides, walkthroughs and the getting-started checklist for two audiences:
// the project team and experts. A screen is named by its sitemap screen name
// ("Event", "Expert Hours"), the `screen` of a COVERAGE row.
import { z } from "zod";
import { uuid } from "../schemas/common";
import { text, textOrEmpty, type Id } from "./common";

export const HELP_AUDIENCES = ["team", "expert"] as const;
export type HelpAudience = (typeof HELP_AUDIENCES)[number];

export type HelpSection = { h: string; p: string[]; list: string[] };

/** A guide. Team ids start with "a-", expert ids with "e-". */
export type HelpArticle = {
  id: string;
  audience: HelpAudience;
  title: string;
  summary: string;
  /** The screen shown as the preview. */
  screen: string;
  /** Walkthrough ids shown as cards under the guide's preview. */
  walks: string[];
  sections: HelpSection[];
  /** Guide or walkthrough ids. */
  related: string[];
  /** True once someone edited it in the app (a new release no longer overwrites it). */
  edited: boolean;
};

/** One step: the screen, a one-line caption, and optionally a few sentences that explain it (S-011). */
export type HelpWalkthroughStep = { screen: string; caption: string; detail?: string };

/** A walkthrough ("w-..."): screens shown one after the other. */
export type HelpWalkthrough = {
  id: string;
  audience: HelpAudience;
  title: string;
  summary: string;
  steps: HelpWalkthroughStep[];
  edited: boolean;
};

export type HelpChecklistItem = {
  key: string;
  title: string;
  /** A guide or walkthrough id. */
  target: string;
  done: boolean;
  /** Ticked by Convenor because the thing was actually done (cannot be unticked). */
  auto: boolean;
};

/** GET /api/help */
export type HelpContent = {
  audience: HelpAudience;
  /** The audiences this person may switch between (both only for people with both roles). */
  audiences: HelpAudience[];
  articles: HelpArticle[];
  walkthroughs: HelpWalkthrough[];
  checklist: HelpChecklistItem[];
  /** The article ids this person answered "Was this helpful?" for. */
  answered: string[];
  supportEmail: string;
  /** A platform admin may edit guides and walkthroughs. */
  canEdit: boolean;
};

export const helpAudienceQuery = z.object({ audience: z.enum(HELP_AUDIENCES).optional() });
export const helpSearchQuery = z.object({ q: z.string().trim().min(1).max(200), audience: z.enum(HELP_AUDIENCES).optional() });

export type HelpSearchHit = {
  kind: "guide" | "walkthrough";
  id: string;
  audience: HelpAudience;
  title: string;
  /** Text around the first match. */
  snippet: string;
  /** Match ranges within the snippet: [start, end). */
  ranges: [number, number][];
};
export type HelpSearchResult = { q: string; hits: HelpSearchHit[] };

export const helpProgressBody = z.object({ done: z.boolean() });

export const helpFeedbackBody = z.object({ articleId: z.string().min(1).max(40), helpful: z.boolean() });

export const HELP_TOPICS = [
  "A question about how something works",
  "Something is not working",
  "Onboarding a project",
  "Access, accounts and roles",
  "Billing",
  "Something else",
] as const;
export type HelpTopic = (typeof HELP_TOPICS)[number];

export const helpContactBody = z.object({
  topic: z.enum(HELP_TOPICS),
  message: text(5000, "Message"),
  /** "Include the page I came from": the in-app path, or null. */
  fromPath: z.string().max(500).nullable(),
  projectId: uuid.nullable(),
  /** Support assistant hand-over (docs/v3/ASSISTANT.md 9): the chat, attached only when attachChat is true. */
  chatId: uuid.optional(),
  attachChat: z.boolean().optional(),
});
export type HelpContactResult = { id: Id };

const helpSectionBody = z.object({
  h: text(200, "Heading"),
  p: z.array(textOrEmpty(3000)).max(20),
  list: z.array(text(500, "List item")).max(30),
});

export const helpArticleBody = z.object({
  title: text(200, "Title"),
  summary: text(500, "Summary"),
  sections: z.array(helpSectionBody).min(1).max(20),
});

export const helpWalkthroughBody = z.object({
  title: text(200, "Title"),
  summary: text(500, "Summary"),
  /** One caption per step, in step order; the screens stay as they are. */
  captions: z.array(text(500, "Caption")).min(1).max(20),
  /** One detail per step ("" for none), in step order; left out keeps the details as they are. */
  details: z.array(textOrEmpty(2000, "Detail")).max(20).optional(),
});
